/**
 * Apache ECharts option factories for Fix indicators.
 *
 * Each factory returns a complete ECharts option object built from the
 * shared theme. Use these instead of writing options inline so all charts
 * stay visually consistent.
 */
import {
  colors,
  palette,
  baseTooltip,
  baseAxisTooltip,
  baseLegend,
  baseGrid,
  baseAxis,
  animation,
} from './echartsTheme'

// ── Bar chart ────────────────────────────────────────────────────────────

/**
 * Vertical bar chart with rounded corners and optional goal line.
 *
 * @param {Object}   p
 * @param {string[]} p.categories     - X-axis labels
 * @param {Array<{name:string,data:number[],color?:string,stack?:string}>} p.series
 * @param {number}   [p.goal]         - Horizontal goal line value (0–100 for %)
 * @param {boolean}  [p.percent=false]- Format Y axis as %
 * @param {boolean}  [p.showLegend=true]
 * @param {string}   [p.yAxisName]
 * @returns {Object} ECharts option
 */
export function buildBarOption({
  categories,
  series,
  goal,
  percent = false,
  showLegend = true,
  yAxisName,
}) {
  const formatter = percent ? (v) => `${v}%` : (v) => v

  const echartsSeries = series.map((s, i) => {
    // If `colors` (array) is provided, use per-bar coloring via data items.
    // Otherwise fall back to the single `color` (or palette).
    const useVariedColors = Array.isArray(s.colors) && s.colors.length > 0
    const data = useVariedColors
      ? s.data.map((v, di) => ({
          value: v,
          itemStyle: {
            color: s.colors[di % s.colors.length],
            borderRadius: [6, 6, 0, 0],
          },
        }))
      : s.data

    return {
      name: s.name,
      type: 'bar',
      stack: s.stack,
      data,
      barMaxWidth: 36,
      itemStyle: useVariedColors
        ? { borderRadius: [6, 6, 0, 0] }
        : {
            color: s.color || palette[i % palette.length],
            borderRadius: [6, 6, 0, 0],
          },
      emphasis: {
        disabled: true,
      },
      label: {
        show: series.length <= 1,
        position: 'top',
        color: colors.text,
        fontSize: 11,
        formatter: ({ value }) => (value > 0 ? formatter(value) : ''),
      },
    }
  })

  if (goal !== undefined && goal !== null) {
    echartsSeries[0] = {
      ...echartsSeries[0],
      markLine: {
        symbol: 'none',
        lineStyle: { color: colors.goal, width: 2, type: 'dashed' },
        label: {
          formatter: `Meta: ${formatter(goal)}`,
          position: 'insideEndTop',
          color: colors.goal,
          fontSize: 11,
        },
        data: [{ yAxis: goal }],
      },
    }
  }

  return {
    color: palette,
    animationDuration: animation.duration,
    animationEasing: animation.easing,
    tooltip: {
      ...baseAxisTooltip,
      valueFormatter: formatter,
    },
    legend: showLegend ? baseLegend : { show: false },
    grid: { ...baseGrid, top: 40, bottom: showLegend ? 56 : 24 },
    xAxis: {
      type: 'category',
      data: categories,
      ...baseAxis,
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      name: yAxisName,
      nameTextStyle: { color: colors.text, fontWeight: 600, fontSize: 12 },
      ...baseAxis,
      axisLabel: { ...baseAxis.axisLabel, formatter },
      max: percent ? 100 : undefined,
    },
    series: echartsSeries,
  }
}

// ── Horizontal bar chart ─────────────────────────────────────────────────

const HORIZONTAL_BAR_VISIBLE_CATEGORIES = 12
const HORIZONTAL_BAR_LABEL_MAX_CHARS = 22

/**
 * Horizontal bar chart for rankings / top-N lists.
 *
 * When there are more categories than fit the chart height, a vertical
 * dataZoom keeps only `HORIZONTAL_BAR_VISIBLE_CATEGORIES` on screen at a
 * time (scroll / drag to reach the rest) instead of squeezing every bar
 * into the same space. Long category names are ellipsized on the axis and
 * shown in full in the tooltip.
 *
 * @param {Object}   p
 * @param {string[]} p.categories - Y-axis labels
 * @param {Array<{name:string,data:number[],color?:string}>} p.series
 * @param {boolean}  [p.showLegend=true]
 */
export function buildHorizontalBarOption({
  categories,
  series,
  showLegend = true,
}) {
  const categoryCount = (categories || []).length
  const isCrowded = categoryCount > HORIZONTAL_BAR_VISIBLE_CATEGORIES

  const echartsSeries = series.map((s, i) => {
    const useVariedColors = Array.isArray(s.colors) && s.colors.length > 0
    const data = useVariedColors
      ? s.data.map((v, di) => ({
          value: v,
          itemStyle: {
            color: s.colors[di % s.colors.length],
            borderRadius: [0, 6, 6, 0],
          },
        }))
      : s.data

    return {
    name: s.name,
    type: 'bar',
    data,
    barMaxWidth: 28,
    itemStyle: useVariedColors
      ? { borderRadius: [0, 6, 6, 0] }
      : {
          color: s.color || palette[i % palette.length],
          borderRadius: [0, 6, 6, 0],
        },
    label: {
      show: series.length <= 1 && !isCrowded,
      position: 'right',
      color: colors.text,
      fontSize: 11,
      formatter: ({ value }) => (value > 0 ? value : ''),
    },
    }
  })

  const zoomEnd = isCrowded
    ? (HORIZONTAL_BAR_VISIBLE_CATEGORIES / categoryCount) * 100
    : 100

  return {
    color: palette,
    animationDuration: animation.duration,
    animationEasing: animation.easing,
    tooltip: baseAxisTooltip,
    legend: showLegend ? baseLegend : { show: false },
    grid: { ...baseGrid, left: 8, right: isCrowded ? 24 : 32 },
    dataZoom: isCrowded
      ? [
          {
            type: 'slider',
            yAxisIndex: 0,
            start: 0,
            end: zoomEnd,
            width: 12,
            right: 4,
            brushSelect: false,
            borderColor: 'transparent',
            fillerColor: 'rgba(42, 142, 164, 0.25)',
            handleStyle: { color: colors.primary },
            showDetail: false,
            zoomLock: true,
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            start: 0,
            end: zoomEnd,
            zoomOnMouseWheel: false,
            moveOnMouseWheel: true,
            moveOnMouseMove: false,
          },
        ]
      : undefined,
    xAxis: {
      type: 'value',
      ...baseAxis,
    },
    yAxis: {
      type: 'category',
      data: categories,
      inverse: true,
      ...baseAxis,
      axisLabel: {
        ...baseAxis.axisLabel,
        width: 140,
        overflow: 'truncate',
        ellipsis: '…',
        formatter: (value) =>
          typeof value === 'string' && value.length > HORIZONTAL_BAR_LABEL_MAX_CHARS
            ? `${value.slice(0, HORIZONTAL_BAR_LABEL_MAX_CHARS - 1)}…`
            : value,
      },
      splitLine: { show: false },
    },
    series: echartsSeries,
  }
}

// ── Pie / donut chart ────────────────────────────────────────────────────

/**
 * Donut chart with rounded segments and percentage labels.
 *
 * @param {Object} p
 * @param {Array<{name:string,value:number,color?:string}>} p.data
 * @param {boolean} [p.showLegend=true]
 * @param {string} [p.title]
 */
export function buildPieOption({ data, showLegend = true, title }) {
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0)

  const seriesData = data.map((d, i) => ({
    name: d.name,
    value: d.value,
    itemStyle: { color: d.color || palette[i % palette.length] },
  }))

  return {
    color: palette,
    animationDuration: animation.duration,
    animationEasing: animation.easing,
    title: title
      ? {
          text: title,
          left: 'center',
          top: 8,
          textStyle: { fontSize: 13, color: colors.text },
        }
      : undefined,
    tooltip: {
      ...baseTooltip,
      formatter: ({ name, value, percent }) =>
        `<b>${name}</b><br/>${value} (${percent}%)`,
    },
    legend: showLegend
      ? {
          ...baseLegend,
          orient: 'horizontal',
          type: 'scroll',
        }
      : { show: false },
    series: [
      {
        type: 'pie',
        radius: ['45%', '72%'],
        center: ['50%', showLegend ? '45%' : '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 6,
          borderColor: '#FFFFFF',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: ({ percent }) => (percent >= 4 ? `${percent}%` : ''),
          color: '#FFFFFF',
          fontWeight: 'bold',
          fontSize: 11,
          position: 'inside',
        },
        labelLine: { show: false },
        emphasis: {
          itemStyle: { shadowBlur: 12, shadowColor: 'rgba(0,0,0,0.18)' },
        },
        data: seriesData,
      },
    ],
  }
}

// ── Line chart ───────────────────────────────────────────────────────────

/**
 * Smooth line chart for time-series data.
 */
export function buildLineOption({
  categories,
  series,
  percent = false,
  showLegend = true,
}) {
  const formatter = percent ? (v) => `${v}%` : (v) => v

  const echartsSeries = series.map((s, i) => ({
    name: s.name,
    type: 'line',
    smooth: true,
    data: s.data,
    symbol: 'circle',
    symbolSize: 8,
    itemStyle: { color: s.color || palette[i % palette.length] },
    lineStyle: { width: 3 },
    areaStyle: s.area
      ? {
          opacity: 0.15,
          color: s.color || palette[i % palette.length],
        }
      : undefined,
  }))

  return {
    color: palette,
    animationDuration: animation.duration,
    tooltip: { ...baseAxisTooltip, valueFormatter: formatter },
    legend: showLegend ? baseLegend : { show: false },
    grid: { ...baseGrid, bottom: showLegend ? 56 : 24 },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      ...baseAxis,
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      ...baseAxis,
      axisLabel: { ...baseAxis.axisLabel, formatter },
      max: percent ? 100 : undefined,
    },
    series: echartsSeries,
  }
}

// ── Gauge chart ──────────────────────────────────────────────────────────

/**
 * Single-value gauge for percentages (used in resolutividade etc.).
 *
 * @param {Object} p
 * @param {number} p.value          - Current value (0–100)
 * @param {number} [p.goal]         - Goal threshold to color-code
 * @param {string} [p.label]
 */
export function buildGaugeOption({ value, goal, label }) {
  const safeValue = Number.isFinite(value) ? value : 0
  const hasGoal = goal !== undefined && goal !== null
  const meetsGoal = !hasGoal || safeValue >= goal
  const mainColor = meetsGoal ? colors.accent : colors.warningSoft

  // Highlight the goal area in the axis-line gradient (if a goal is set).
  const axisLineColors = hasGoal
    ? [
        [Math.max(0, Math.min(1, goal / 100)), '#F4D5D5'],
        [1, '#E5F2D5'],
      ]
    : [[1, colors.border]]

  return {
    animationDuration: animation.duration,
    tooltip: {
      ...baseTooltip,
      formatter: () =>
        hasGoal
          ? `<b>${safeValue.toFixed(1)}%</b><br/>Meta: ${goal}%`
          : `<b>${safeValue.toFixed(1)}%</b>`,
    },
    series: [
      {
        type: 'gauge',
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max: 100,
        radius: '90%',
        center: ['50%', '62%'],
        splitNumber: 10,
        progress: {
          show: true,
          width: 22,
          roundCap: true,
          itemStyle: {
            color: mainColor,
            shadowColor: 'rgba(0,0,0,0.15)',
            shadowBlur: 8,
          },
        },
        axisLine: {
          lineStyle: { width: 22, color: axisLineColors },
          roundCap: true,
        },
        axisTick: { show: false },
        splitLine: {
          show: true,
          length: 10,
          distance: -28,
          lineStyle: { color: '#FFFFFF', width: 2 },
        },
        axisLabel: {
          show: true,
          color: colors.textSoft,
          fontSize: 11,
          distance: -42,
          formatter: (v) => (v % 20 === 0 ? `${v}` : ''),
        },
        pointer: { show: false },
        anchor: { show: false },
        title: {
          show: !!label,
          offsetCenter: [0, '-32%'],
          color: colors.textSoft,
          fontSize: 13,
          fontWeight: 500,
        },
        detail: {
          valueAnimation: true,
          formatter: (v) => `${(v || 0).toFixed(1)}%`,
          fontSize: 38,
          fontWeight: 'bold',
          color: meetsGoal ? colors.text : colors.warningSoft,
          offsetCenter: [0, '12%'],
        },
        data: [{ value: safeValue, name: label || '' }],
      },
      // Goal marker: small pointer at the goal threshold
      ...(hasGoal
        ? [
            {
              type: 'gauge',
              startAngle: 210,
              endAngle: -30,
              min: 0,
              max: 100,
              radius: '90%',
              center: ['50%', '62%'],
              axisLine: { show: false },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              progress: { show: false },
              pointer: {
                show: true,
                length: '15%',
                width: 4,
                offsetCenter: [0, '-72%'],
                itemStyle: { color: colors.goal },
              },
              anchor: { show: false },
              title: {
                show: true,
                offsetCenter: [0, '38%'],
                color: colors.goal,
                fontSize: 12,
                fontWeight: 600,
              },
              detail: { show: false },
              data: [{ value: goal, name: `Meta: ${goal}%` }],
            },
          ]
        : []),
    ],
  }
}
